import * as React from "react";
import { Box, Typography } from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import LockOpenOutlinedIcon from "@mui/icons-material/LockOpenOutlined";
import { useReopenFieldAdminHSDayMutation } from "./fieldAdminApi";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import { CARD_BG, BORDER, AMBER } from "../../Global/colors";
import { OK_GREEN_TEXT, FONT_FAMILY, teamLabel, installerLabel, parseLocalDate, formatTimeOfDay } from "./designTokens";

// Home's two summary panels: the day-complete card that replaces the "continue" banner
// once the H&S sequence is signed off, and the team strip beside the greeting. Both are
// self-contained read-only panels, and Home.js was 467 lines with them inline.

export function DayCompleteCard({ day, qe, completedCount, onView }) {
  const [reopenDay, { isLoading: isReopening }] = useReopenFieldAdminHSDayMutation();
  const signedOff = formatTimeOfDay(day.completed_at);
  return (
    <Box sx={{ backgroundColor: "rgba(34,197,94,0.06)", border: "1px solid rgba(34,197,94,0.28)", borderRadius: "14px", p: "18px 20px", mb: "22px" }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: "12px", mb: "14px" }}>
        <Box sx={{ width: "40px", height: "40px", borderRadius: "10px", backgroundColor: "rgba(34,197,94,0.14)", color: OK_GREEN_TEXT, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          <CheckCircleOutlineIcon sx={{ fontSize: "1.3rem" }} />
        </Box>
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: "0.95rem", fontWeight: 700, color: "#fff" }}>Day complete · signed off {signedOff}</Typography>
          <Typography sx={{ fontSize: "0.78rem", color: "rgba(255,255,255,0.55)", mt: "2px" }}>
            {teamLabel(day.team_name, day.team_id)} · {parseLocalDate(day.date).toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short" })}
          </Typography>
        </Box>
      </Box>

      {/* Three cells, not the design's four: its "Stock closing" needs the COC-scoped
          figure StockBalanceView cannot yet derive (see the close-out screen). */}
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(3, 1fr)" }, gap: "10px", mb: "12px" }}>
        <DayCompleteCell label="H&S workflow" value={`${completedCount} / 5`} sub="sections done" />
        <DayCompleteCell label="Attendance" value={`${day.attendance_verified} / ${day.attendance_total}`} sub="verified" />
        <DayCompleteCell label="Installs" value={qe.installs_done_today} sub={`${qe.cocs_issued_today} COC'd by QE`} />
      </Box>

      <Box sx={{ display: "flex", alignItems: "center", gap: "18px", flexWrap: "wrap" }}>
        <Box
          component="button"
          type="button"
          onClick={onView}
          sx={{ background: "none", border: "none", p: 0, cursor: "pointer", fontFamily: FONT_FAMILY, fontSize: "0.76rem", fontWeight: 600, color: OK_GREEN_TEXT }}
        >
          View H&S summary →
        </Box>
        {/* Deliberately quiet: reopening undoes a sign-off, so it sits beside the primary
            action in muted text rather than competing with it. */}
        <Box
          component="button"
          type="button"
          disabled={isReopening}
          onClick={() => reopenDay()}
          sx={{
            display: "flex", alignItems: "center", gap: "6px",
            background: "none", border: "none", p: 0,
            cursor: isReopening ? "default" : "pointer", fontFamily: FONT_FAMILY,
            fontSize: "0.76rem", fontWeight: 600, color: "rgba(255,255,255,0.45)",
            "&:hover": { color: "rgba(255,255,255,0.75)" },
          }}
        >
          <LockOpenOutlinedIcon sx={{ fontSize: "0.9rem" }} />
          {isReopening ? "Reopening…" : "Reopen day"}
        </Box>
      </Box>
    </Box>
  );
}

export function DayCompleteCell({ label, value, sub }) {
  return (
    <Box sx={{ backgroundColor: "rgba(255,255,255,0.025)", border: "1px solid rgba(255,255,255,0.05)", borderRadius: "10px", px: "14px", py: "12px" }}>
      <Typography sx={{ fontSize: "0.62rem", fontWeight: 700, color: "rgba(255,255,255,0.45)", textTransform: "uppercase", letterSpacing: "0.06em", mb: "4px" }}>{label}</Typography>
      <Typography sx={{ fontSize: "1.1rem", fontWeight: 700, color: "#fff", fontVariantNumeric: "tabular-nums", letterSpacing: "-0.01em" }}>{value}</Typography>
      <Typography sx={{ fontSize: "0.7rem", color: "rgba(255,255,255,0.45)", mt: "1px" }}>{sub}</Typography>
    </Box>
  );
}

// index.html .team-strip — team identity, three linked .team-cells, and the .target-bar.
export function TeamStrip({ overview, vehicle, onNavigate }) {
  const target = overview.install_target;
  const pct = target ? Math.min(100, Math.round((overview.installs_done / target) * 1000) / 10) : null;

  return (
    <Box sx={{ backgroundColor: CARD_BG, border: `1px solid ${BORDER}`, borderRadius: "14px", px: "18px", py: "16px" }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "10px", mb: "12px" }}>
        <Typography sx={{ fontSize: "0.95rem", fontWeight: 700, color: "#fff" }}>
          {teamLabel(overview.team_name, overview.team_id)}
        </Typography>
        {overview.area_name ? (
          <Box
            component="button"
            type="button"
            onClick={() => onNavigate("/field-admin/sites/")}
            sx={{ display: "inline-flex", alignItems: "center", gap: "5px", background: "none", border: "none", p: 0, cursor: "pointer", fontFamily: FONT_FAMILY, fontSize: "0.72rem", color: "rgba(255,255,255,0.5)", "&:hover": { color: AMBER } }}
          >
            <PlaceOutlinedIcon sx={{ fontSize: "0.8rem" }} />
            {overview.area_name}
          </Box>
        ) : null}
      </Box>

      <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "8px" }}>
        <TeamCell label="Installers" value={overview.installer_count} sub={`${overview.verified_count} verified`} onClick={() => onNavigate("/field-admin/more/team/")} />
        <TeamCell label="QE" value={overview.qe ? 1 : 0} sub={overview.qe ? installerLabel(overview.qe.name, overview.qe.ims_installer_id) : "None assigned"} onClick={() => onNavigate("/field-admin/more/qe-oversight/")} />
        <TeamCell
          label="Vehicle"
          value={vehicle ? vehicle.registration : "—"}
          valueSx={{ fontSize: "0.9rem" }}
          sub={vehicle ? vehicle.make_model : "None assigned"}
          onClick={() => onNavigate("/field-admin/more/fleet/")}
        />
      </Box>

      {target ? (
        <Box sx={{ mt: "12px", pt: "12px", borderTop: "1px solid rgba(255,255,255,0.05)" }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", fontSize: "0.74rem", color: "rgba(255,255,255,0.55)", mb: "6px" }}>
            <Typography component="span" sx={{ fontSize: "0.74rem", color: "rgba(255,255,255,0.55)" }}>
              Target · <Box component="span" sx={{ color: "#fff", fontWeight: 600, fontVariantNumeric: "tabular-nums" }}>{overview.installs_done}&nbsp;/&nbsp;{target}</Box> installs
            </Typography>
            {/* The design's "8-wk plan · wk 4 of 8" is omitted: IMS's TargetInstalls
                carries a bare number with no period to frame it honestly. */}
            <Typography component="span" sx={{ fontSize: "0.68rem", color: "rgba(255,255,255,0.4)" }}>{pct}%</Typography>
          </Box>
          <Box sx={{ height: "6px", backgroundColor: "rgba(255,255,255,0.06)", borderRadius: "3px", overflow: "hidden" }}>
            <Box sx={{ height: "100%", width: `${pct}%`, background: `linear-gradient(90deg, ${AMBER}, #E0A020)`, borderRadius: "3px" }} />
          </Box>
        </Box>
      ) : null}
    </Box>
  );
}

export function TeamCell({ label, value, sub, valueSx, onClick }) {
  return (
    <Box
      component="button"
      type="button"
      onClick={onClick}
      sx={{
        textAlign: "left", backgroundColor: "rgba(255,255,255,0.025)", border: "1px solid rgba(255,255,255,0.04)",
        borderRadius: "8px", px: "12px", py: "10px", cursor: "pointer", fontFamily: FONT_FAMILY, minWidth: 0,
        "&:hover": { borderColor: "rgba(253,185,64,0.35)" },
      }}
    >
      <Typography sx={{ fontSize: "0.62rem", fontWeight: 600, color: "rgba(255,255,255,0.4)", textTransform: "uppercase", letterSpacing: "0.06em", mb: "4px" }}>{label}</Typography>
      <Typography sx={{ fontSize: "0.95rem", fontWeight: 700, color: "#fff", fontVariantNumeric: "tabular-nums", letterSpacing: "-0.01em", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", ...valueSx }}>{value}</Typography>
      <Typography sx={{ fontSize: "0.7rem", color: "rgba(255,255,255,0.45)", mt: "1px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{sub}</Typography>
    </Box>
  );
}

// Field ops tile — headline value (white, unlike the amber stat tiles above) + a
// color-coded breakdown line with dot separators, matching index.html's .ops-tile.
